import { rd, RemoteData } from '@passionware/monads';
import type { ResourceType } from '__types__';
import { EyeOff, Globe, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { Button } from 'v5/design-system/Button';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { ResourceTypeIcon } from 'v5/patterns/resource/ResourceTypeIcon';
import { cn } from 'v5/platform/dom/cn';
import type { CreateDraft } from './useResourceFormModel';

export interface CreateDraftSummaryProps {
  draft: CreateDraft;
  resourceTypes: RemoteData<ResourceType[]>;
  workspaces: RemoteData<EnterpriseWorkspace[]>;
  /** "Change" button; omitted when there is nowhere to go back to. */
  onEdit?: () => void;
  editDisabled?: boolean;
  className?: string;
}

/**
 * Recap of the wizard's first step (type, picture, visibility). The create
 * dialog pins it under its title so the long form never loses the context it
 * is being filled in.
 */
export function CreateDraftSummary({
  draft,
  resourceTypes,
  workspaces,
  onEdit,
  editDisabled,
  className
}: CreateDraftSummaryProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'AssignResourcesToWorkspacesDialog'
  });
  const resourceType = rd.tryMap(resourceTypes, types =>
    types.find(type => type.id === draft.resourceTypeId)
  );
  const workspaceList = rd.tryGet(workspaces) ?? [];
  const memberWorkspaces =
    draft.membership.type === 'workspaces'
      ? draft.membership.workspaceIds.map(
          id => workspaceList.find(workspace => workspace.id === id) ?? null
        )
      : [];

  return (
    <div
      className={cn('flex items-center gap-4', className)}
      data-testid="resource-create-summary"
    >
      {draft.profilePicture ? (
        <img
          src={draft.profilePicture}
          alt=""
          className="size-10 shrink-0 rounded-lg object-cover"
        />
      ) : (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bg-subtle">
          <ResourceTypeIcon icon={resourceType?.icon} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2 text-base font-medium text-fg-default">
          {draft.profilePicture && (
            <ResourceTypeIcon icon={resourceType?.icon} />
          )}
          <span className="truncate">{resourceType?.name ?? '…'}</span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-muted">
          {draft.membership.type === 'hidden' && (
            <span className="flex items-center gap-1.5">
              <EyeOff className="size-4 shrink-0" />
              {t('scopeHidden')}
            </span>
          )}
          {draft.membership.type === 'global' && (
            <span className="flex items-center gap-1.5">
              <Globe className="size-4 shrink-0" />
              {t('scopeGlobal')}
            </span>
          )}
          {draft.membership.type === 'workspaces' &&
            memberWorkspaces.map((workspace, index) =>
              workspace ? (
                <span
                  key={workspace.id}
                  className="inline-flex items-center gap-1.5 rounded-md bg-bg-subtle px-2 py-0.5 text-fg-default"
                >
                  <EnterpriseWorkspaceIcon
                    name={workspace.name}
                    iconUrl={workspace.iconUrl}
                    size="sm"
                  />
                  {workspace.name}
                </span>
              ) : (
                <span key={`missing-${index}`}>…</span>
              )
            )}
        </div>
      </div>
      {onEdit && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onEdit}
          disabled={editDisabled}
        >
          <Pencil className="size-4" />
          Change
        </Button>
      )}
    </div>
  );
}
